<template>
  <v-card variant="outlined" rounded="lg" class="flex flex-col px-3 py-3 border-theme sm:px-4">
    <div class="flex items-center gap-2 sm:gap-3">
      <v-icon icon="mdi-file-document-outline" class="text-medium-emphasis" />
      <div class="grow min-w-0">
        <div class="text-sm font-medium">{{ t(`treezorOnboarding.documents.${labelKey}`) }}</div>
        <div class="text-xs text-medium-emphasis">
          {{
            t("treezorOnboarding.documents.uploadedOn", {
              date: formatDocumentDate(document.createdAt),
            })
          }}
        </div>
      </div>
      <v-chip
        v-if="user.isKind('admin')"
        class="flex-none"
        size="small"
        variant="tonal"
        :color="documentStatusColor(document.status)"
      >
        {{ documentStatusLabel(document.status) }}
        <v-tooltip v-if="document.status === PreReviewDocumentStatus.Pending" activator="parent">
          {{ t("treezorOnboarding.documents.pendingTooltip") }}
        </v-tooltip>
      </v-chip>
      <div class="flex flex-none items-center">
        <v-btn
          v-if="user.isKind('admin')"
          size="small"
          variant="text"
          icon
          class="text-medium-emphasis"
          :aria-label="t('treezorOnboarding.documents.review')"
          :disabled="busy"
          @click="openReviewConfirm(document, labelKey, userId)"
        >
          <v-icon icon="mdi-file-check-outline" />
          <v-tooltip activator="parent">{{ t("treezorOnboarding.documents.review") }}</v-tooltip>
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          icon
          color="primary"
          :aria-label="t('common.download')"
          :disabled="busy"
          @click="onViewDocument(document, userId)"
        >
          <v-icon icon="mdi-download" />
          <v-tooltip activator="parent">{{ t("common.download") }}</v-tooltip>
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          icon
          color="error"
          :aria-label="t('treezorOnboarding.documents.delete')"
          :disabled="!canEditKyc || busy"
          @click="openDeleteConfirm(document, labelKey, userId)"
        >
          <v-icon icon="mdi-delete-outline" />
          <v-tooltip activator="parent">{{ t("treezorOnboarding.documents.delete") }}</v-tooltip>
        </v-btn>
      </div>
    </div>
    <v-alert
      v-if="user.isKind('admin') && document.comment"
      class="mt-3"
      icon-size="small"
      type="info"
      :text="t('treezorOnboarding.documents.reviewNote', { comment: document.comment })"
    />
  </v-card>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useUserStore } from "@/store";
import { PreReviewDocumentStatus, type PreReviewDocument } from "@aircap/common/treezor";
import { computed } from "vue";
import { useTreezorOnboarding, type DocumentLabelKey } from "./useTreezorOnboarding";

const props = defineProps<{
  document: PreReviewDocument;
  labelKey: DocumentLabelKey;
  userId?: number;
}>();

const { t, locale } = useI18n();
const user = useUserStore();
const {
  canEditKyc,
  deletingDocumentId,
  reviewingDocumentId,
  onViewDocument,
  openDeleteConfirm,
  openReviewConfirm,
} = useTreezorOnboarding();

const busy = computed(
  () =>
    deletingDocumentId.value === props.document.documentId ||
    reviewingDocumentId.value === props.document.documentId,
);

const formatDocumentDate = (createdAt: string) => {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return createdAt;
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(parsed);
};

const documentStatusColor = (status: PreReviewDocument["status"]) => {
  switch (status) {
    case PreReviewDocumentStatus.Pending:
      return "warning";
    case PreReviewDocumentStatus.Refused:
      return "error";
    case PreReviewDocumentStatus.Validated:
      return "success";
  }
};

const documentStatusLabel = (status: PreReviewDocument["status"]) => {
  switch (status) {
    case PreReviewDocumentStatus.Pending:
      return t("treezorOnboarding.kyc.statuses.PENDING");
    case PreReviewDocumentStatus.Refused:
      return t("treezorOnboarding.kyc.statuses.REFUSED");
    case PreReviewDocumentStatus.Validated:
      return t("treezorOnboarding.kyc.statuses.VALIDATED");
  }
};
</script>
